import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { OrgAdminApi } from "../../api/OrgAdminApi";
import FormContent from "../../components/organization/orgGrpFormSchema.jsx";
import { isError, getErrorMsg, validateForm } from "../../helper/FormHelper.jsx";
import { confirmDeleteGrp } from "./UserGrpListPage";

const LIST_PATH = "/user/grps";

export default function UserGrpDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [record, setRecord] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const formik = useFormik(FormContent.FormikInstance);

  useEffect(() => {
    const grpId = Number(id);
    if (!Number.isInteger(grpId) || grpId <= 0) {
      setIsLoading(false);
      setLoadError("找不到社別");
      return;
    }

    setIsLoading(true);
    setLoadError("");
    OrgAdminApi.getGrpCallback(grpId, (isSuccess, msg, data) => {
      setIsLoading(false);
      if (!isSuccess || !data) {
        setRecord(null);
        setLoadError(typeof msg === "string" && msg ? msg : "找不到社別");
        return;
      }

      setRecord(data);
      formik.setValues({
        grp_code: data.grp_code || "",
        name_zh: data.name_zh || "",
        name_en: data.name_en || "",
        display_pos: data.display_pos ?? 0,
      });
    });
  }, [id]);

  const handleUpdate = async () => {
    if (!record?.id) {
      return;
    }
    if (await validateForm(formik)) return;

    const payload = {
      grp_code: formik.values.grp_code.trim(),
      name_zh: formik.values.name_zh.trim(),
      name_en: (formik.values.name_en || "").trim(),
      display_pos: Number(formik.values.display_pos),
    };

    OrgAdminApi.updateGrpCallback(record.id, payload, (isSuccess, msg, data) => {
      if (!isSuccess || !data) {
        toast.error(typeof msg === "string" && msg ? msg : "更新失敗");
        return;
      }

      setRecord(data);
      formik.setValues({
        grp_code: data.grp_code || "",
        name_zh: data.name_zh || "",
        name_en: data.name_en || "",
        display_pos: data.display_pos ?? 0,
      });
      toast.success("更新成功");
    });
  };

  const handleDelete = () => {
    if (!record?.id) {
      return;
    }

    confirmDeleteGrp(record.id, () => {
      navigate(LIST_PATH);
    });
  };

  return (
    <Container maxWidth="lg">
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography variant="h4" sx={{ mt: 2, mb: 3 }}>
        社別
      </Typography>

      <Paper sx={{ px: 3, py: 4 }} elevation={3}>
        {loadError ? (
          <Stack spacing={2} alignItems="flex-start">
            <Typography>{loadError}</Typography>
            <Button variant="outlined" onClick={() => navigate(LIST_PATH)}>
              返回列表
            </Button>
          </Stack>
        ) : null}

        {record ? (
          <Stack spacing={3} component="form" onSubmit={(event) => event.preventDefault()}>
            {FormContent.FormControl.map((item) => {
              const field = item.props;
              return (
                <TextField
                  key={field.id}
                  {...field}
                  name={field.id}
                  value={formik.values[field.id] ?? ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={isError(formik, field.id)}
                  helperText={getErrorMsg(formik, field.id)}
                  fullWidth
                />
              );
            })}
            <Stack direction="row" spacing={2}>
              <Button variant="contained" onClick={handleUpdate}>
                更新
              </Button>
              <Button variant="outlined" color="error" onClick={handleDelete}>
                刪除
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="text" onClick={() => navigate(LIST_PATH)}>
                返回列表
              </Button>
            </Stack>
          </Stack>
        ) : null}
      </Paper>
    </Container>
  );
}
